import { faDollarSign } from '@fortawesome/free-solid-svg-icons/faDollarSign';
import { faPercent } from '@fortawesome/free-solid-svg-icons/faPercent';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Dialog, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Discount } from '@spike/order-model';
import clsx from 'clsx';
import { Button, PercentageField2, PriceField2 } from 'components/UI';
import { FunctionComponent, useState } from 'react';
import DiscountTypeButton from './DiscountTypeButton';

export interface DiscountModalProps {
    discount?: Discount;
    total: number;
    onChange?: (discount: Discount) => void;
    onClose?: () => void;
}

const amount = 'amount';
const percentage = 'percentage';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialog: {
            '& .MuiPaper-rounded': {
                borderRadius: '24px !important'
            }
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            width: 429,
            height: 264,
            borderRadius: 24,
            backgroundColor: '#FFF',
            border: '1px solid #FFF'
        },
        header: {
            display: 'flex',
            paddingTop: 25
        },
        body: {
            display: 'flex',
            position: 'relative',
            flex: 1,
            alignItems: 'center'
        },
        footer: {
            display: 'flex',
            height: 81,
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
            borderTop: '1px solid #DDD',
            alignItems: 'center'
        },
        horizontalPadding: {
            paddingLeft: 36,
            paddingRight: 36
        },
        leftContainer: {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center'
        },
        rightContainer: {
            display: 'flex',
            flex: 1,
            justifyContent: 'flex-end',
            alignItems: 'center'
        },
        title: {
            fontSize: 18,
            fontWeight: 600
        },
        closeIcon: {
            cursor: 'pointer'
        },
        addButton: {
            width: 160
        },
        totalLabel: {
            display: 'inline-block',
            fontSize: 15
        },
        totalAmount: {
            display: 'inline-block',
            fontWeight: 600,
            marginLeft: 6
        },
        percentageButton: {
            marginLeft: 12,
            marginRight: 12
        },
        errorMessageContainer: {
            position: 'absolute',
            left: 36,
            bottom: 7
        },
        errorMessage: {
            fontSize: 14,
            fontWeight: 500,
            color: '#EF4F57'
        }
    })
);

export const DiscountModal: FunctionComponent<DiscountModalProps> = props => {
    const classes = useStyles(props);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [type, setType] = useState<typeof amount | typeof percentage>(
        props.discount?.percentage !== undefined ? 'percentage' : 'amount'
    );

    const [value, setValue] = useState<number | undefined>(0);

    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState('');

    const addHandler = () => {
        if (type === amount && (value ?? 0) > props.total) {
            setErrorMessage(`Discount can be maximum $${props.total.toFixed(2)}`);
        } else if (type === percentage && (value ?? 0) > 100) {
            setErrorMessage(`Discount can be maximum 100%`);
        } else {
            setErrorMessage('');
            setSaving(true);
            //save discount.
        }
    };

    const changeType = (type: typeof amount | typeof percentage) => {
        setType(type);
        setValue(0);
    };

    const header = (
        <Box className={clsx(classes.header, classes.horizontalPadding)}>
            <Box className={classes.leftContainer}>
                <Typography className={classes.title}>Add Discount</Typography>
            </Box>
            <Box className={classes.rightContainer}>
                {!saving && (
                    <FontAwesomeIcon
                        icon={faTimes}
                        fontSize={24}
                        className={classes.closeIcon}
                        onClick={props.onClose}
                    />
                )}
            </Box>
        </Box>
    );

    const body = (
        <Box className={clsx(classes.body, classes.horizontalPadding)}>
            <Box className={classes.leftContainer}>
                <DiscountTypeButton
                    icon={faDollarSign}
                    selected={type === amount}
                    onSelected={() => changeType(amount)}
                />
                <DiscountTypeButton
                    icon={faPercent}
                    selected={type === percentage}
                    className={classes.percentageButton}
                    onSelected={() => changeType(percentage)}
                />
            </Box>
            <Box className={classes.rightContainer}>
                {type === amount && (
                    <PriceField2
                        value={0}
                        scale={2}
                        onChange={setValue}
                    />
                )}
                {type === percentage && (
                    <PercentageField2
                        value={0}
                        precision={3}
                        onChange={setValue}
                    />
                )}
            </Box>
            <Box className={classes.errorMessageContainer}>
                <Typography className={classes.errorMessage}>{errorMessage}</Typography>
            </Box>
        </Box>
    );

    const footer = (
        <Box className={clsx(classes.footer, classes.horizontalPadding)}>
            <Box className={classes.leftContainer}>
                <Typography className={classes.totalLabel}>
                    New total:<Typography className={classes.totalAmount}>$290.00</Typography>
                </Typography>
            </Box>
            <Box className={classes.rightContainer}>
                <Button
                    label="Add"
                    color="green"
                    loading={saving}
                    className={classes.addButton}
                    onClick={addHandler}
                />
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={true}
            onClose={props.onClose}
            fullScreen={isMobile}
            className={classes.dialog}
        >
            <Box className={classes.content}>
                {header}
                {body}
                {footer}
            </Box>
        </Dialog>
    );
};

export default DiscountModal;
